import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Plus, Trash2 } from "lucide-react";

import Button from "../../../shared/ui/button/Button";
import {
    DEFAULT_DURATION,
    MAX_TITLE,
    PAST_SESSION_STATUS_CHOICES,
    isDuration,
    isWallClock,
    statusFields,
    todayIso,
    SUBJECT_ID_FIELD,
    type ImportCourseOption,
    type ImportSubjectKind,
    type ImportSubjectOption,
    type ImportPractitionerOption,
    type PastSessionInput,
    type PastSessionPayment,
    type PastSessionResult,
    type PastSessionStatusChoice,
    type PastSessionUbication,
} from "../domain/pastSessionImport";

/** A row as the form holds it: everything as typed. */
interface RowDraft {
    key: number;
    /** `kind:id` — a patient, a couple or a group. */
    subject: string;
    /** Empty: the patient's own practitioner. */
    therapistId: string;
    title: string;
    date: string;
    hour: string;
    duration: string;
    status: PastSessionStatusChoice;
    payment: PastSessionPayment;
    ubication: PastSessionUbication;
    moduleId: string;
    /** What the API refused this row with, once sent. */
    refusal?: string;
}

type RowField = "subject" | "date" | "hour" | "duration";

const SUBJECT_KINDS: ImportSubjectKind[] = ["patient", "couple", "group"];

let nextKey = 0;

const blankRow = (from?: RowDraft, subject = ""): RowDraft => ({
    key: nextKey++,
    subject,
    therapistId: "",
    title: "",
    date: from?.date ?? "",
    hour: from?.hour ?? "",
    duration: from?.duration ?? String(DEFAULT_DURATION),
    status: "finished",
    payment: from?.payment ?? "paid",
    ubication: from?.ubication ?? "in person",
    moduleId: "",
});

const FIELD_CLASS =
    "h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-content-primary dark:border-gray-700";

function invalidFields(row: RowDraft, today: string): RowField[] {
    const invalid: RowField[] = [];
    if (!row.subject) invalid.push("subject");
    if (!row.date || row.date > today) invalid.push("date");
    if (!isWallClock(row.hour)) invalid.push("hour");
    if (!isDuration(Number(row.duration))) invalid.push("duration");
    return invalid;
}

function toInput(row: RowDraft): PastSessionInput {
    const [kind, id] = row.subject.split(":") as [ImportSubjectKind, string];
    return {
        [SUBJECT_ID_FIELD[kind]]: id,
        ...(row.title.trim() ? { title: row.title.trim() } : {}),
        ...(row.therapistId ? { therapist_id: row.therapistId } : {}),
        date: row.date,
        hour: row.hour,
        duration: Number(row.duration),
        ...statusFields(row.status),
        payment_status: row.payment,
        ubication: row.ubication,
        ...(row.moduleId ? { course_module_id: row.moduleId } : {}),
    };
}

interface Props {
    subjects: ImportSubjectOption[];
    /** `kind:id` of the one patient, couple or group every row is about; no picker then. */
    fixedSubject?: string;
    /** A centre's form names who held each session. */
    practitioners?: ImportPractitionerOption[];
    /** A training centre's form names the module each worked on. */
    courses?: ImportCourseOption[];
    send: (rows: PastSessionInput[]) => Promise<PastSessionResult[]>;
    sending: boolean;
    errorMessage: (code: string) => string;
}

/**
 * Past sessions typed in, one row each. A new row starts from the last one's
 * date, hour, length, payment and modality: a history is usually typed in
 * runs. Rows the API takes leave the form; the ones it refuses stay, saying why.
 */
export default function PastSessionRowsForm({ subjects, fixedSubject, practitioners, courses, send, sending, errorMessage }: Props) {
    const { t } = useTranslation();
    const [rows, setRows] = useState<RowDraft[]>(() => [blankRow(undefined, fixedSubject)]);
    const [submitted, setSubmitted] = useState(false);
    const [created, setCreated] = useState<number | null>(null);
    const [failed, setFailed] = useState(false);
    const today = todayIso();

    const change = (key: number, changes: Partial<RowDraft>) =>
        setRows((current) =>
            current.map((row) => (row.key === key ? { ...row, ...changes, refusal: undefined } : row))
        );
    const addRow = () =>
        setRows((current) => [...current, blankRow(current[current.length - 1], fixedSubject)]);
    const removeRow = (key: number) => setRows((current) => current.filter((row) => row.key !== key));

    const handleSubmit = async () => {
        setSubmitted(true);
        setFailed(false);
        setCreated(null);
        if (rows.length === 0 || rows.some((row) => invalidFields(row, today).length > 0)) return;
        try {
            const results = await send(rows.map(toInput));
            const refusedRows = rows
                .map((row, index) => ({ ...row, refusal: results[index]?.error ?? undefined }))
                .filter((row) => row.refusal);
            setCreated(rows.length - refusedRows.length);
            setRows(refusedRows.length ? refusedRows : [blankRow(rows[rows.length - 1], fixedSubject)]);
            setSubmitted(false);
        } catch {
            setFailed(true);
        }
    };

    return (
        <form
            className="flex flex-col gap-4"
            noValidate
            onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
            }}
        >
            <ol className="flex flex-col gap-3">
                {rows.map((row, index) => {
                    const invalid = submitted ? invalidFields(row, today) : [];
                    const id = (field: string) => `past-session-${row.key}-${field}`;
                    const errorId = id("error");
                    const fieldProps = (field: RowField) => ({
                        id: id(field),
                        "aria-invalid": invalid.includes(field) || undefined,
                        "aria-describedby": invalid.includes(field) ? errorId : undefined,
                    });
                    return (
                        <li key={row.key}>
                            <fieldset className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
                                <legend className="px-1 text-sm font-semibold text-content-primary">
                                    {t("pastSessions.rowN", { n: index + 1 })}
                                </legend>
                                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                    {!fixedSubject && (
                                    <div className="col-span-2">
                                        <label htmlFor={id("subject")} className="mb-1 block text-xs font-medium text-content-secondary">
                                            {t("pastSessions.subject")}
                                        </label>
                                        <select
                                            {...fieldProps("subject")}
                                            className={FIELD_CLASS}
                                            value={row.subject}
                                            onChange={(e) => change(row.key, { subject: e.target.value })}
                                        >
                                            <option value="">{t("pastSessions.chooseSubject")}</option>
                                            {SUBJECT_KINDS.map((kind) => {
                                                const ofKind = subjects.filter((subject) => subject.kind === kind);
                                                return ofKind.length === 0 ? null : (
                                                    <optgroup key={kind} label={t(`pastSessions.kinds.${kind}`)}>
                                                        {ofKind.map((subject) => (
                                                            <option key={subject.id} value={`${kind}:${subject.id}`}>
                                                                {subject.label}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    )}
                                    <div className="col-span-2">
                                        <label htmlFor={id("title")} className="mb-1 block text-xs font-medium text-content-secondary">
                                            {t("pastSessions.sessionTitle")}
                                        </label>
                                        <input
                                            id={id("title")}
                                            type="text"
                                            maxLength={MAX_TITLE}
                                            placeholder={t("pastSessions.sessionTitlePlaceholder")}
                                            className={FIELD_CLASS}
                                            value={row.title}
                                            onChange={(e) => change(row.key, { title: e.target.value })}
                                        />
                                    </div>
                                    {practitioners && (
                                        <div className="col-span-2">
                                            <label htmlFor={id("therapist")} className="mb-1 block text-xs font-medium text-content-secondary">
                                                {t("pastSessions.practitioner")}
                                            </label>
                                            <select
                                                id={id("therapist")}
                                                className={FIELD_CLASS}
                                                value={row.therapistId}
                                                onChange={(e) => change(row.key, { therapistId: e.target.value })}
                                            >
                                                <option value="">{t("pastSessions.ownPractitioner")}</option>
                                                {practitioners.map((practitioner) => (
                                                    <option key={practitioner.id} value={practitioner.id}>
                                                        {practitioner.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div>
                                        <label htmlFor={id("date")} className="mb-1 block text-xs font-medium text-content-secondary">
                                            {t("pastSessions.date")}
                                        </label>
                                        <input
                                            {...fieldProps("date")}
                                            type="date"
                                            max={today}
                                            className={FIELD_CLASS}
                                            value={row.date}
                                            onChange={(e) => change(row.key, { date: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor={id("hour")} className="mb-1 block text-xs font-medium text-content-secondary">
                                            {t("pastSessions.hour")}
                                        </label>
                                        <input
                                            {...fieldProps("hour")}
                                            type="time"
                                            className={FIELD_CLASS}
                                            value={row.hour}
                                            onChange={(e) => change(row.key, { hour: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor={id("duration")} className="mb-1 block text-xs font-medium text-content-secondary">
                                            {t("pastSessions.duration")}
                                        </label>
                                        <input
                                            {...fieldProps("duration")}
                                            type="number"
                                            min={1}
                                            max={600}
                                            inputMode="numeric"
                                            className={FIELD_CLASS}
                                            value={row.duration}
                                            onChange={(e) => change(row.key, { duration: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor={id("status")} className="mb-1 block text-xs font-medium text-content-secondary">
                                            {t("pastSessions.status")}
                                        </label>
                                        <select
                                            id={id("status")}
                                            className={FIELD_CLASS}
                                            value={row.status}
                                            onChange={(e) => change(row.key, { status: e.target.value as PastSessionStatusChoice })}
                                        >
                                            {PAST_SESSION_STATUS_CHOICES.map((choice) => (
                                                <option key={choice} value={choice}>
                                                    {t(`pastSessions.statuses.${choice === "cancelled:patient" ? "cancelledByPatient" : choice}`)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor={id("payment")} className="mb-1 block text-xs font-medium text-content-secondary">
                                            {t("pastSessions.payment")}
                                        </label>
                                        <select
                                            id={id("payment")}
                                            className={FIELD_CLASS}
                                            value={row.payment}
                                            onChange={(e) => change(row.key, { payment: e.target.value as PastSessionPayment })}
                                        >
                                            <option value="paid">{t("session.paymentOptions.paid")}</option>
                                            <option value="pending">{t("session.paymentOptions.pending")}</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor={id("ubication")} className="mb-1 block text-xs font-medium text-content-secondary">
                                            {t("pastSessions.ubication")}
                                        </label>
                                        <select
                                            id={id("ubication")}
                                            className={FIELD_CLASS}
                                            value={row.ubication}
                                            onChange={(e) => change(row.key, { ubication: e.target.value as PastSessionUbication })}
                                        >
                                            <option value="in person">{t("pastSessions.inPerson")}</option>
                                            <option value="online">{t("pastSessions.online")}</option>
                                        </select>
                                    </div>
                                    {courses && courses.length > 0 && (
                                        <div className="col-span-2">
                                            <label htmlFor={id("module")} className="mb-1 block text-xs font-medium text-content-secondary">
                                                {t("pastSessions.module")}
                                            </label>
                                            <select
                                                id={id("module")}
                                                className={FIELD_CLASS}
                                                value={row.moduleId}
                                                onChange={(e) => change(row.key, { moduleId: e.target.value })}
                                            >
                                                <option value="">{t("pastSessions.noModule")}</option>
                                                {courses.map((course) => (
                                                    <optgroup key={course.id} label={course.label}>
                                                        {course.modules.map((module) => (
                                                            <option key={module.id} value={module.id}>
                                                                {module.label}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-3 flex items-start justify-between gap-3">
                                    <div id={errorId} className="text-sm text-red-600 dark:text-red-400">
                                        {invalid.length > 0 && (
                                            <p role="alert">
                                                {invalid
                                                    .map((field) => t(`pastSessions.invalid.${field}`))
                                                    .join(" · ")}
                                            </p>
                                        )}
                                        {row.refusal && <p role="alert">{errorMessage(row.refusal)}</p>}
                                    </div>
                                    {rows.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRow(row.key)}
                                            aria-label={t("pastSessions.removeRow", { n: index + 1 })}
                                            className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
                                        >
                                            <Trash2 size={15} aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                            </fieldset>
                        </li>
                    );
                })}
            </ol>

            <div aria-live="polite">
                {created !== null && created > 0 && (
                    <p className="flex items-center gap-1.5 text-sm font-medium text-content-primary">
                        <CheckCircle2 size={16} className="text-brand-600 dark:text-brand-300" aria-hidden="true" />
                        {t("pastSessions.created", { count: created })}
                    </p>
                )}
                {failed && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                        {t("pastSessions.failed")}
                    </p>
                )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <Button type="button" variant="outline" size="sm" onClick={addRow} startIcon={<Plus size={14} aria-hidden="true" />}>
                    {t("pastSessions.addRow")}
                </Button>
                <Button type="submit" disabled={sending} loading={sending}>
                    {t("pastSessions.submit", { count: rows.length })}
                </Button>
            </div>
        </form>
    );
}
